import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useAppAuth } from '@/hooks/useAppAuth';

export function useBookmarks() {
  const auth = useAppAuth(false);
  const [bookmarkedIds, setBookmarkedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  const loadBookmarks = useCallback(async () => {
    if (!auth.userId) return;
    setLoading(true);
    try {
      const { data } = await supabase
        .from('caregiver_bookmarks')
        .select('caregiver_user_id')
        .eq('user_id', auth.userId);
      if (data) {
        setBookmarkedIds(new Set(data.map((b) => b.caregiver_user_id)));
      }
    } catch (err) {
      console.error('Failed to load bookmarks:', err);
    } finally {
      setLoading(false);
    }
  }, [auth.userId]);

  useEffect(() => {
    loadBookmarks();
  }, [loadBookmarks]);

  const toggleBookmark = useCallback(async (caregiverUserId: string) => {
    if (!auth.userId) return;
    const isBookmarked = bookmarkedIds.has(caregiverUserId);

    // Optimistic update
    setBookmarkedIds((prev) => {
      const next = new Set(prev);
      if (isBookmarked) {
        next.delete(caregiverUserId);
      } else {
        next.add(caregiverUserId);
      }
      return next;
    });

    try {
      if (isBookmarked) {
        await supabase
          .from('caregiver_bookmarks')
          .delete()
          .eq('user_id', auth.userId)
          .eq('caregiver_user_id', caregiverUserId);
      } else {
        await supabase
          .from('caregiver_bookmarks')
          .insert({
            user_id: auth.userId,
            caregiver_user_id: caregiverUserId,
          });
      }
    } catch (err) {
      console.error('Failed to toggle bookmark:', err);
      // Revert on failure
      loadBookmarks();
    }
  }, [auth.userId, bookmarkedIds, loadBookmarks]);

  const isBookmarked = useCallback((caregiverUserId: string) => {
    return bookmarkedIds.has(caregiverUserId);
  }, [bookmarkedIds]);

  return { bookmarkedIds, loading, toggleBookmark, isBookmarked, refresh: loadBookmarks };
}